import cn from 'classnames';
import { forwardRef } from 'react';

import { useTableSectionContext } from '../table/table-section-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableCellProps } from './table-cell.types';

import './table-cell.scss';

/**
 * The component renders a `<th>` element when the parent context is a header
 * or otherwise a `<td>` element.
 *
 * ### Usage
 *
 * ```tsx
 * import { Table, TableHeader, TableBody, TableFoot, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomTable() {
 *   return (
 *     <Table>
 *       <TableHeader>
 *         <TableRow>
 *           <TableCell>This cell will be rendered as `<th>`</TableCell>
 *         </TableRow>
 *       </TableHeader>
 *       <TableBody>
 *         <TableRow>
 *           <TableCell>This cell will be rendered as `<td>`</TableCell>
 *         </TableRow>
 *      </TableBody>
 *      <TableFoot>
 *         <TableRow>
 *           <TableCell>This cell will be rendered as `<td>`</TableCell>
 *         </TableRow>
 *      </TableFoot>
 *    </Table>
 *   );
 * }
 * ```
 */
const TableCell = forwardRef(
  (props: TableCellProps, forwardedRef: ForwardedRef<HTMLTableCellElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const sectionContext = useTableSectionContext();

    const isHeadCell = sectionContext?.variant === 'head';
    const isBodyCell = sectionContext?.variant === 'body';
    const isFootCell = sectionContext?.variant === 'foot';

    const CellComponent = isHeadCell ? 'th' : 'td';

    const classes = cn(
      'rbui-table-cell',
      {
        'rbui-table-cell__body': isBodyCell,
        'rbui-table-cell__foot': isFootCell,
        'rbui-table-cell__foot--has-pagination': isFootCell && sectionContext?.hasPagination,
        'rbui-table-cell__head': isHeadCell,
      },
      className,
    );

    return (
      <CellComponent className={classes} ref={forwardedRef} {...rest}>
        {children}
      </CellComponent>
    );
  },
);

TableCell.displayName = 'TableCell';

export default TableCell;
